import React, { useEffect, useMemo } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Requisition } from '@/components/shared/requisitions/associate-req/associateReq.interface';
import { RequisitionItem } from '@/components/shared/requisitions/associate-req/components/RequisitionItem';
import { EmptyResults } from '@/components/ats';
import { NoReq } from '@/assets/icons/ats/empty';
import { useQuery } from '@tanstack/react-query';
import { API_SERVICE, API_KEYS } from '@/components/shared/requisitions/api';

interface AssociateReqListProps {
  requisitions?: Requisition[];
  selectedReqs: Requisition[];
  users?: any[];
  onSelectReq: (req: Requisition) => void;
  setReqListState?: (reqs: Requisition[]) => void;
  offerId?: string;
  jobAssociated?: boolean;
  showGroupDialog?: number | null;
  onSelectAllGroup?: (req: Requisition) => void;
  onSelectSingle?: (req: Requisition) => void;
}

export const AssociateReqList: React.FC<AssociateReqListProps> = ({
  requisitions,
  selectedReqs,
  users,
  onSelectReq,
  setReqListState,
  offerId,
  jobAssociated,
  showGroupDialog,
  onSelectAllGroup,
  onSelectSingle,
}) => {
  // Filter out requisitions that have associated - MEMOIZED to prevent recreating array on every render
  const requisitionsList = useMemo(() => {
    return requisitions?.filter(
      (req) =>
        !req?.requisition_association?.id ||
        req?.requisition_association?.offer?.id === Number(offerId)
    );
  }, [requisitions, offerId]);

  // Check if any requisition is part of a group
  const hasGroupedRequisitions = useMemo(() => {
    return requisitionsList?.some(
      (req) => req.requisition_group_id || (req as any).group?.id
    );
  }, [requisitionsList]);

  // Fetch all requisitions to get complete group data
  const { data: allRequisitionsData } = useQuery({
    queryKey: [
      API_KEYS.getRequisitions,
      { page: 1, limit: 500, sort_key: 'created_at', sort_order: 'desc' },
    ],
    queryFn: () =>
      API_SERVICE.getRequisitions({
        page: 1,
        limit: 500,
        sort_key: 'created_at',
        sort_order: 'desc',
      }),
    enabled: hasGroupedRequisitions,
    staleTime: 0,
  });

  // Merge fetched requisitions with provided requisitions
  const completeRequisitionsList = useMemo(() => {
    if (!hasGroupedRequisitions) {
      return requisitionsList || [];
    }

    const fetchedReqs = allRequisitionsData?.requisitions || [];

    // Create a map to deduplicate
    const reqMap = new Map();

    // Add fetched requisitions first (complete data)
    fetchedReqs.forEach((fetchedReq: Requisition) => {
      reqMap.set(fetchedReq.id, fetchedReq);
    });

    // Add requisitionsList (may have some missing from fetch)
    requisitionsList?.forEach((r) => {
      if (!reqMap.has(r.id)) {
        reqMap.set(r.id, r);
      }
    });

    return Array.from(reqMap.values());
  }, [hasGroupedRequisitions, allRequisitionsData, requisitionsList]);

  // Transform complete requisitions list to ensure consistent data structure
  const transformedAllRequisitions = useMemo(() => {
    return completeRequisitionsList.map((r) => ({
      ...r,
      requisition_group_id: (r as any).group?.id || r.requisition_group_id,
      group_size: (r as any).group?.size || r.group_size,
      code: r.code || (r as any).requisition_value,
    }));
  }, [completeRequisitionsList]);

  useEffect(() => {
    if (setReqListState && requisitionsList) {
      setReqListState(requisitionsList);
    }
  }, [requisitionsList, setReqListState]);

  if (jobAssociated && requisitionsList?.length === 0) {
    return (
      <Flex justifyContent="center" alignItems="center" h="100%">
        <EmptyResults
          icon={NoReq}
          wrapperProps={{ h: '100%', flex: 1, maxW: '48%' }}
          text="Turn off the toggle above to see all available requisitions, or create a new one"
          iconProps={{ h: '140px', w: '140px', mb: '0px' }}
          subText="No requisitions linked to this job"
          subTextProps={{ size: 'md-semi-bold', color: 'secondary-900' }}
          textProps={{
            size: 'sm-regular',
            color: 'secondary-700',
            textAlign: 'center',
          }}
        />
      </Flex>
    );
  }

  if (requisitionsList?.length === 0) {
    return (
      <Flex justifyContent="center" alignItems="center" h="100%">
        <EmptyResults
          icon={NoReq}
          wrapperProps={{ h: '100%', flex: 1 }}
          text="No requisitions found"
          iconProps={{ h: '140px', w: '140px', mb: '0px' }}
          subText="LOOKS EMPTY IN HERE"
        />
      </Flex>
    );
  }

  return (
    <Box>
      {requisitionsList?.map((req) => {
        const showDialog = showGroupDialog === req.id;
        // Consider item as selected if it's actually selected OR if the dialog is showing for it
        const isSelected =
          selectedReqs?.some((item) => item.id === req.id) || showDialog;
        const recruiter = users?.find((user) => user.id === req?.recruiter?.id);
        const hiringManager = users?.find(
          (user) => user.id === req?.hiring_manager?.id
        );

        return (
          <RequisitionItem
            key={req.id}
            req={req}
            isSelected={isSelected}
            recruiter={recruiter}
            hiringManager={hiringManager}
            onSelect={onSelectReq}
            allRequisitions={transformedAllRequisitions}
            selectedReqs={selectedReqs}
            showGroupDialog={showDialog}
            onSelectAllGroup={() => onSelectAllGroup?.(req)}
            onSelectSingle={() => onSelectSingle?.(req)}
          />
        );
      })}
    </Box>
  );
};
